import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { GhostWidget } from "@/components/ghost/GhostWidget";
import { Button } from "@/components/ui/button";
import { ArrowLeft, AlertCircle, Globe, MessageCircle } from "lucide-react";
import { Helmet } from "react-helmet";

const Preview = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [iframeError, setIframeError] = useState(false);
  
  const websiteUrl = searchParams.get("url") || "";
  const chosenColor = searchParams.get("color") || "#8b5cf6";

  // Decode the URL
  const decodedUrl = decodeURIComponent(websiteUrl);

  // Extract domain for display
  const getDomain = (url: string) => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  };

  if (!decodedUrl) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto" />
          <h1 className="text-2xl font-bold text-white">No URL provided</h1>
          <p className="text-gray-400">Please provide a website URL to preview.</p>
          <Button onClick={() => navigate("/")} variant="outline">
            Go Back Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Widget Preview | Feednatic</title>
        <meta name="description" content="Preview the Feednatic chat widget on your website" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-100 relative">
        {/* Top bar */}
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-purple-900 to-blue-900 text-white py-3 px-4 flex items-center justify-between shadow-lg">
          <Button 
            onClick={() => navigate("/")} 
            variant="ghost" 
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Feednatic
          </Button>
          <div className="text-center flex-1">
            <span className="text-sm opacity-80">Previewing widget on:</span>
            <span className="ml-2 font-medium">{getDomain(decodedUrl)}</span>
          </div>
          <div className="w-[140px]" /> {/* Spacer for centering */}
        </div>

        {/* iframe container or fallback */}
        <div className="pt-14 h-screen">
          {iframeError ? (
            <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-200 flex flex-col items-center justify-center p-8">
              <div className="max-w-md text-center space-y-6">
                <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center mx-auto">
                  <Globe className="w-10 h-10 text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {getDomain(decodedUrl)} restricts embedding
                </h2>
                <p className="text-gray-600">
                  Some websites block iframe embedding for security reasons. Don't worry — the widget will work perfectly once installed on your actual site!
                </p>
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: chosenColor }}
                    >
                      <MessageCircle className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-800">Your widget preview</p>
                      <p className="text-sm text-gray-500">Click the button to try it →</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">
                    Enter your email in the widget to get started with Feednatic!
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <iframe
              src={decodedUrl}
              className="w-full h-full border-0"
              title="Website Preview"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              referrerPolicy="no-referrer"
              onError={() => setIframeError(true)}
              onLoad={(e) => {
                // Try to detect if iframe loaded properly
                try {
                  const iframe = e.target as HTMLIFrameElement;
                  // If we can't access contentWindow, it might be blocked
                  if (!iframe.contentWindow?.location.href) {
                    setIframeError(true);
                  }
                } catch {
                  // Cross-origin error means it loaded (even if we can't access it)
                  // This is actually fine - the iframe loaded successfully
                }
              }}
            />
          )}
        </div>

        {/* Ghost Widget Overlay */}
        <div className="fixed bottom-4 right-4 z-[9999]">
          <GhostWidget primaryColor={chosenColor} websiteUrl={decodedUrl} />
        </div>
      </div>
    </>
  );
};

export default Preview;
